import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const PaymentProcessorIntegration = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, loading: authLoading } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    processorType: 'stripe',
    processorName: '',
    isTestMode: true,
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
    accountEmail: ''
  });

  useEffect(() => {
    if (!authLoading && (!user || !isGymOwner)) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isGymOwner) {
      loadIntegrations();
    }
  }, [currentTenant, isGymOwner]);

  const loadIntegrations = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase?.from('payment_processor_integrations')?.select('*')?.eq('tenant_id', currentTenant?.id)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setIntegrations(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load integrations');
      console.error('Load integrations error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const { error: saveError } = await supabase?.from('payment_processor_integrations')?.upsert([{
          tenant_id: currentTenant?.id,
          processor_type: formData?.processorType,
          processor_name: formData?.processorName || (formData?.processorType === 'stripe' ? 'Stripe' : 'PayPal'),
          is_test_mode: formData?.isTestMode,
          publishable_key_encrypted: formData?.publishableKey,
          secret_key_encrypted: formData?.secretKey,
          webhook_secret_encrypted: formData?.webhookSecret,
          account_email: formData?.accountEmail,
          is_active: false,
          created_by: user?.id
        }], {
          onConflict: 'tenant_id,processor_type'
        });

      if (saveError) throw saveError;

      setSuccess('Payment processor integration saved successfully!');
      setShowAddModal(false);
      setFormData({
        processorType: 'stripe',
        processorName: '',
        isTestMode: true,
        publishableKey: '',
        secretKey: '',
        webhookSecret: '',
        accountEmail: ''
      });
      loadIntegrations();
    } catch (err) {
      setError(err?.message || 'Failed to save integration');
      console.error('Save integration error:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleIntegrationStatus = async (integrationId, currentStatus) => {
    try {
      const { error: updateError } = await supabase?.from('payment_processor_integrations')?.update({ is_active: !currentStatus })?.eq('id', integrationId);

      if (updateError) throw updateError;
      loadIntegrations();
    } catch (err) {
      setError(err?.message || 'Failed to update integration status');
      console.error('Toggle status error:', err);
    }
  };

  const deleteIntegration = async (integrationId) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;

    try {
      const { error: deleteError } = await supabase?.from('payment_processor_integrations')?.delete()?.eq('id', integrationId);

      if (deleteError) throw deleteError;
      setSuccess('Integration deleted successfully');
      loadIntegrations();
    } catch (err) {
      setError(err?.message || 'Failed to delete integration');
      console.error('Delete integration error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/gym-settings')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Settings
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Payment Processor Integration</h1>
              <p className="text-muted-foreground">Connect Stripe or PayPal to accept payments from members</p>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Add Integration
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <p className="text-success text-sm">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {integrations?.map((integration) => (
            <div key={integration?.id} className="bg-card rounded-xl shadow-md p-6 border border-border">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    integration?.processor_type === 'stripe' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                  }`}>
                    <Icon
                      name={integration?.processor_type === 'stripe' ? 'CreditCard' : 'Wallet'}
                      size={24}
                      color={integration?.processor_type === 'stripe' ? '#8B5CF6' : '#3B82F6'}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {integration?.processor_name || integration?.processor_type}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {integration?.is_test_mode ? 'Test Mode' : 'Live Mode'}
                    </p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  integration?.is_active
                    ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  {integration?.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {integration?.account_email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{integration?.account_email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Key" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">
                    {integration?.publishable_key_encrypted ? 'Keys configured' : 'No keys configured'}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleIntegrationStatus(integration?.id, integration?.is_active)}
                  fullWidth
                >
                  {integration?.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteIntegration(integration?.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            </div>
          ))}

          {integrations?.length === 0 && (
            <div className="col-span-2 bg-card rounded-xl shadow-md p-12 text-center">
              <Icon name="CreditCard" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Payment Processors Configured</h3>
              <p className="text-muted-foreground mb-6">
                Add Stripe or PayPal integration to start accepting payments
              </p>
              <Button onClick={() => setShowAddModal(true)} iconName="Plus" iconPosition="left">
                Add Your First Integration
              </Button>
            </div>
          )}
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-foreground">Add Payment Processor</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddModal(false)}
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Processor *
                  </label>
                  <Select
                    value={formData?.processorType}
                    onChange={(e) => setFormData({ ...formData, processorType: e?.target?.value })}
                    required
                  >
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Account Email *
                  </label>
                  <Input
                    type="email"
                    value={formData?.accountEmail}
                    onChange={(e) => setFormData({ ...formData, accountEmail: e?.target?.value })}
                    placeholder="your-email@example.com"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="testMode"
                    checked={formData?.isTestMode}
                    onChange={(e) => setFormData({ ...formData, isTestMode: e?.target?.checked })}
                    className="w-4 h-4 rounded border-border"
                  />
                  <label htmlFor="testMode" className="text-sm text-foreground">
                    Test Mode (Use test API keys)
                  </label>
                </div>

                {formData?.processorType === 'stripe' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Publishable Key *
                      </label>
                      <Input
                        type="text"
                        value={formData?.publishableKey}
                        onChange={(e) => setFormData({ ...formData, publishableKey: e?.target?.value })}
                        placeholder="pk_test_..."
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Find this in your Stripe Dashboard under Developers → API keys
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Secret Key *
                      </label>
                      <Input
                        type="password"
                        value={formData?.secretKey}
                        onChange={(e) => setFormData({ ...formData, secretKey: e?.target?.value })}
                        placeholder="sk_test_..."
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Keep this secure - never share your secret key
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Webhook Secret (Optional)
                      </label>
                      <Input
                        type="password"
                        value={formData?.webhookSecret}
                        onChange={(e) => setFormData({ ...formData, webhookSecret: e?.target?.value })}
                        placeholder="whsec_..."
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Required for webhook event verification
                      </p>
                    </div>
                  </>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    iconName={saving ? 'Loader2' : 'Check'}
                    iconClassName={saving ? 'animate-spin' : ''}
                    iconPosition="left"
                    fullWidth
                  >
                    {saving ? 'Saving...' : 'Save Integration'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default PaymentProcessorIntegration;
